import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/api'
import { useAuthStore } from './auth'
import { useGameStore } from './game'

export interface StreamResult {
  status?: string
  embedUrl?: string
  phenixEmbedUrl?: string
  error?: string
  message?: string
  available?: boolean
  startDate?: string
  title?: string
}

export const STREAMABLE_GAME_SLUGS = new Set([
  'uk-49s-brunchtime-6-49',
  'uk-49s-lunchtime-6-49',
  'uk-49s-drivetime-6-49',
  'uk-49s-teatime-6-49',
  'uk39s',
  'fast15s',
])

export const LIVE_STREAM_MODULE = 'live_stream'

export function isStreamableGameSlug(slug: string): boolean {
  return STREAMABLE_GAME_SLUGS.has(slug) || slug.startsWith('uk-49s-')
}

export function isLiveStreamEnabledForAccount(enabledModules?: string[] | null): boolean {
  return Array.isArray(enabledModules) && enabledModules.includes(LIVE_STREAM_MODULE)
}

export function canShowLiveStream(slug: string, enabledModules?: string[] | null): boolean {
  return isStreamableGameSlug(slug) && isLiveStreamEnabledForAccount(enabledModules)
}

export const useStreamStore = defineStore('stream', () => {
  const isLoading = ref(false)
  const embedUrl = ref('')
  const error = ref('')

  const fetchStreamingVideo = async (gameSlug: string): Promise<boolean> => {
    isLoading.value = true
    error.value = ''
    embedUrl.value = ''

    try {
      const authStore = useAuthStore()
      const gameStore = useGameStore()

      if (!authStore.isAuthenticated) {
        error.value = 'stream.loginRequired'
        return false
      }

      const account = authStore.account || sessionStorage.getItem('auth_account')
      const currency =
        gameStore.getBetSettings?.currency_code || sessionStorage.getItem('auth_currency')

      const headers: Record<string, string> = {}
      if (account) headers['auth-account'] = account
      if (currency) headers['auth-currency'] = currency
      Object.assign(headers, authStore.getAuthHeaders)

      const { data } = await api.post(
        '/stream-video',
        { gameSlug },
        { headers }
      )

      const stream: StreamResult = data?.result?.stream ?? {}

      if (stream.error) {
        error.value = stream.error
        return false
      }

      const url = stream.embedUrl || stream.phenixEmbedUrl
      if (url) {
        embedUrl.value = url
        return true
      }

      error.value = stream.message || 'stream.unavailable'
      return false
    } catch (err: unknown) {
      console.error('Error loading live stream:', err)
      error.value = 'stream.errorLoading'
      return false
    } finally {
      isLoading.value = false
    }
  }

  const clearStream = (): void => {
    embedUrl.value = ''
    error.value = ''
    isLoading.value = false
  }

  return {
    isLoading,
    embedUrl,
    error,
    fetchStreamingVideo,
    clearStream,
  }
})
